/**
 * Projekt zgłoszenia przyłączenia mikroinstalacji do operatora sieci (OSD), wypełniany w przeglądarce.
 *
 * ZASADY (decyzje właściciela 2026-10-07):
 * - Wypełniamy tylko projekt: klient (albo instalator z pełnomocnictwem) sprawdza, podpisuje i wysyła sam.
 * - Dane wnioskodawcy są opcjonalne (etap 2), także PESEL: wpisane zostają w przeglądarce, nie trafiają
 *   na serwer ani do pamięci przeglądarki (decyzja właściciela 2026-10-07, polityka prywatności rozdz. 10d).
 * - Dane klienta nie opuszczają przeglądarki: PDF powstaje lokalnie (pdf-lib), nic nie idzie na serwer.
 * - Nie dopisujemy do wniosku nic od siebie (ani nazwy serwisu, ani uwag).
 *
 * Funkcje mapujące są czyste i testowane; `wypelnijPdf` ładuje pdf-lib dopiero przy generowaniu.
 * Mapowanie na wzory pozostałych operatorów: `osd/<klucz>.js`, lista plików do pobrania: `osd/index.js`.
 */

import { ocenMocy } from './mocPrzylaczeniowa'

/** Liczba po polsku (przecinek), zaokrąglona; pusty napis, gdy brak wartości. */
export const liczbaPl = (v, miejsc = 2) => {
  if (v == null || !Number.isFinite(Number(v))) return ''
  return String(Math.round(Number(v) * 10 ** miejsc) / 10 ** miejsc).replace('.', ',')
}

// Tokeny z cyframi, które nie są kodem modelu: jednostki, normy, technologie (jak backend `nazwy_seo.py`).
const NIE_KOD = /^(\d+(?:[.,]\d+)?(?:w|wp|kw|kwp|kwh|wh|v|a|ah|mm|kg|szt|kva|va|%)?|ip\d+|lifepo4|lfp|mppt\d*|\d+-?mppt|\d+-?f|\d+-?faz\w*|n-?typ\w*|\d+bb|g\d|gen\d|x\d+|\d+x)$/i

const WARIANT = /^(pro|plus|max|ultra|mini)(-[a-z0-9]{1,3})?$/i

/** Kod modelu z nazwy ze sklepu („SUN-10K-SG05LP3-EU”), albo null. Do pól o ograniczonej szerokości. */
export const kodModelu = (nazwa, producent) => {
  const prod = (producent || '').toLowerCase().replace(/[\s-]/g, '')
  const tokeny = String(nazwa || '').replace(/(?<=\w)[‐-–](?=\w)/g, '-')
    .split(/[\s,;()[\]|]+|\/\//).map((t) => t.replace(/^\d+[*×]/, '').replace(/^[.,:;"']+|[.,:;"']+$/g, '')).filter(Boolean)
  const kandydaci = tokeny.filter((t) => /\d/.test(t) && /[a-z]/i.test(t) && t.length >= 4 && !t.includes(':')
    && !NIE_KOD.test(t) && t.toLowerCase().replace(/-/g, '') !== prod)
  if (!kandydaci.length) return null
  const kod = kandydaci.reduce((a, b) => (b.length > a.length ? b : a))
  // Wariant po kodzie należy do modelu: „SE-G5.1 Pro-B” to inny magazyn niż „SE-G5.1”.
  const nastepny = tokeny[tokeny.indexOf(kod) + 1]
  return nastepny && WARIANT.test(nastepny) ? `${kod} ${nastepny}` : kod
}

/** Moc znamionowa wszystkich falowników (kW): ta moc może trafić do sieci. */
export const mocFalownikow = (f) => (f?.mocKw ? f.mocKw * (f.sztuk || 1) : null)

export const cyfryPesel = (p) => String(p || '').replace(/\D/g, '')

/** PESEL: 11 cyfr i zgodna cyfra kontrolna (wagi 1, 3, 7, 9). Literówki nie trafiają do wniosku. */
export const peselPoprawny = (p) => {
  const c = cyfryPesel(p)
  if (c.length !== 11) return false
  const wagi = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3]
  const suma = wagi.reduce((s, w, i) => s + w * Number(c[i]), 0)
  return (10 - (suma % 10)) % 10 === Number(c[10])
}

const jedenKandydat = (sekcja) => (sekcja?.kandydaci?.length === 1 ? sekcja.kandydaci[0] : null)

/** Podział [(n, łańcuchów), ...] z backendu na listę wejść MPPT { mppt, modulow, lancuchow }. */
const wejsciaMppt = (podzial) => (podzial || []).map(([modulow, lancuchow], i) => ({ mppt: i + 1, modulow, lancuchow }))

/**
 * Łańcuchy z „Sprawdź ofertę” (`stringi[]`): ten sam silnik co VoltValue Projekt (`electrical/stringi.py`,
 * `sprawdz_liczbe` dla dokładnej liczby modułów), więc zmiana zasad liczenia trafia w oba miejsca.
 * Tylko przy dokładnym modelu modułu i falownika; przy kilku falownikach układ na jeden falownik.
 */
export const lancuchyDoSchematu = (wynik, kF) => {
  const wiersz = kF?.model_id ? (wynik?.stringi || []).find((s) => s.falownik_model_id === kF.model_id) : null
  const l = wiersz?.lancuchy
  if (!wiersz?.ok || !l?.podzial?.length) return null
  return {
    wejscia: wejsciaMppt(l.podzial),
    napiecie: l.napiecie, // 'ok' | 'poza_oknem' | 'za_duzo'
    mppt: l.mppt,
    vocZimnoModulu: l.voc_modulu_zimno_v ?? null,
    vmpModulu: l.vmp_modulu_v ?? null,
    iscModulu: l.isc_modulu_a ?? null,
    iscUpalModulu: l.isc_modulu_upal_a ?? null,
    napiecieMaxZimno: l.napiecie_max_zimno_v ?? null,
    napiecieDcMax: l.napiecie_dc_max_v ?? null,
    mpptMin: l.mppt_v_min ?? null,
    mpptMax: l.mppt_v_max ?? null,
    tMin: l.t_min_c ?? -25,
    tMax: l.t_max_c ?? 70,
    // Kilka falowników: części modułów po równo ([{ modulow, falownikow, wejscia }]).
    falowniki: (wiersz.lancuchy_falownikow || []).map((c) => ({
      modulow: c.modulow, falownikow: c.falownikow, wejscia: wejsciaMppt(c.lancuchy?.podzial),
    })),
  }
}

/**
 * Sprzęt z raportu „Sprawdź ofertę” w kształcie wniosku. Model wpisujemy tylko, gdy jest jeden kandydat
 * (dokładny model); przy kilku zostaje producent i moc, a model do uzupełnienia.
 */
export const sprzetDoZgloszenia = (wynik, wejscie) => {
  const kM = jedenKandydat(wynik?.moduly)
  const kF = jedenKandydat(wynik?.falownik)
  const kG = jedenKandydat(wynik?.magazyn)
  const mocW = Number(wynik?.moduly?.moc_w || kM?.moc_w || wejscie?.moduly?.model?.moc || wejscie?.moduly?.moc) || null
  const liczba = Number(wejscie?.liczbaModulow) || null
  const fazyFalownika = kF?.fazy || (['1', '3'].includes(String(wejscie?.fazy)) ? Number(wejscie.fazy) : null)
  const zMagazynem = Boolean(wejscie?.magazyn && wejscie.magazyn.brak === false)
  const sztukFalownikow = Math.max(1, Math.round(Number(wejscie?.liczbaFalownikow) || 1))
  const kwhSzt = Number(kG?.pojemnosc_kwh || wejscie?.magazyn?.model?.moc || wejscie?.magazyn?.moc) || null
  const sztuk = zMagazynem ? (Number(wejscie?.liczbaMagazynu) || 1) : 0
  return {
    moduly: {
      producent: kM?.producent || wejscie?.moduly?.producent || '',
      model: kM ? (kodModelu(kM.nazwa, kM.producent) || '') : '',
      mocKw: mocW ? mocW / 1000 : null,
      liczba,
      sumaKw: mocW && liczba ? (mocW * liczba) / 1000 : null,
      karta: kM?.karta_url || null,
    },
    falownik: {
      producent: kF?.producent || wejscie?.falownik?.producent || '',
      model: kF ? (kodModelu(kF.nazwa, kF.producent) || '') : '',
      mocKw: Number(kF?.moc_kw || wejscie?.falownik?.model?.moc || wejscie?.falownik?.moc) || null,
      fazy: fazyFalownika === 1 ? 1 : 3,
      fazyZnane: fazyFalownika === 1 || fazyFalownika === 3,
      sztuk: sztukFalownikow,
      // Tylko przy jednym kandydacie: podsumowanie kilku modeli może nieść PDF sąsiedniego modelu.
      certyfikat: kF?.certyfikat || null,
      karta: kF?.karta_url || null,
      typ: kF?.typ || null, // „Hybrydowy”, „On-grid”…: typ falownika magazynu w załącznikach
    },
    // Łańcuchy tylko przy jednym kandydacie modułu: przy kilku backend liczył dla przypadkowego z nich.
    lancuchy: kM ? lancuchyDoSchematu(wynik, kF) : null,
    magazyn: zMagazynem ? {
      producent: kG?.producent || wejscie?.magazyn?.producent || '',
      model: kG ? (kodModelu(kG.nazwa, kG.producent) || '') : '',
      kwhSzt,
      sztuk,
      kwhRazem: kwhSzt ? kwhSzt * sztuk : null,
      // Moc rozładowania z karty (na jednostkę); przy samym prądzie tylko maksimum U × I do wglądu.
      mocKwSzt: kG?.moc_rozladowania_kw || null,
      mocMaksSzt: kG?.moc_maks_z_pradu_kw || null,
      karta: kG?.karta_url || null,
      typ: kG?.typ || null, // „All-In-One” ma własny falownik
      // Parametry z karty do załączników o magazynie (backend `_dane_karty_magazynu`).
      dane: kG?.karta_magazynu || null,
    } : null,
  }
}

/** Dzień, miesiąc, rok z daty ISO albo z obiektu Date. */
export const czesciDaty = (d) => {
  const data = d instanceof Date ? d : (d ? new Date(`${d}T12:00:00`) : null)
  if (!data || Number.isNaN(data.getTime())) return null
  return { dz: String(data.getDate()).padStart(2, '0'), mies: String(data.getMonth() + 1).padStart(2, '0'), rok: String(data.getFullYear()) }
}

/** Długość linii danych wnioskodawcy w Enea WZ-MI (pola „kratkowe”, maxLength 36). */
const KRATKI_WNIOSKODAWCY = 36

/**
 * Kawałki tekstu (w kolejności ważności) w `ileLinii` liniach po `maks` znaków, łączone „, ”.
 * Kawałek, który nie mieści się w bieżącej linii, przechodzi do następnej; po ostatniej linii odpada.
 */
export const rozlozWLinie = (kawalki, ileLinii, maks) => {
  const linie = Array.from({ length: ileLinii }, () => '')
  let i = 0
  for (const k of kawalki.map((x) => String(x || '').trim()).filter(Boolean)) {
    while (i < ileLinii) {
      const proba = linie[i] ? `${linie[i]}, ${k}` : k
      if (proba.length <= maks) { linie[i] = proba; break }
      if (!linie[i]) { linie[i] = k.slice(0, maks); break } // pojedynczy kawałek dłuższy niż linia: przycinamy
      i += 1
    }
  }
  return linie
}

/** Moc magazynu wpisana w formularzu („7,5”) jako liczba kW albo null. */
export const mocMagazynuKlienta = (klient = {}) => Number(String(klient.mocMagazynu ?? '').replace(',', '.')) || null

/**
 * Moc do porównania z przyłączeniową (art. 7 ust. 8d12), wspólna dla wszystkich wzorów: tryb
 * „zgłoszenie” albo „wniosek o warunki”, łączna moc z magazynem, gdy się go dolicza.
 */
export const ocenaWniosku = (sprzet, klient = {}) => ocenMocy({
  mocModulowKw: sprzet.moduly.sumaKw, mocFalownikaKw: mocFalownikow(sprzet.falownik), zMagazynem: Boolean(sprzet.magazyn),
  mocMagazynuKw: mocMagazynuKlienta(klient), mocPrzylaczeniowaKw: klient.mocPrzylaczeniowa, jednofazowa: sprzet.falownik.fazy === 1,
})

/** Adres instalacji: osobny, gdy klient odznaczył „pod tym samym adresem”, inaczej adres wnioskodawcy. */
export const adresInstalacji = (klient = {}) => (klient.instalacjaTaSama === false ? {
  ulica: klient.instalacjaUlica, nr: klient.instalacjaNrDomu, kod: klient.instalacjaKodPocztowy,
  miejscowosc: klient.instalacjaMiejscowosc, gmina: klient.instalacjaGmina,
} : { ulica: klient.ulica, nr: klient.nrDomu, kod: klient.kodPocztowy, miejscowosc: klient.miejscowosc, gmina: klient.gmina })

const TECHNOLOGIE = [
  [/lifepo|lfp|fosforan/i, 'litowo-żelazowo-fosforanowa (LiFePO4)'],
  [/nmc|nickel|niklowo/i, 'litowo-jonowa NMC'],
  [/lto|tytan/i, 'litowo-tytanowa (LTO)'],
  [/sod|na-ion/i, 'sodowo-jonowa'],
  [/kwas|lead|ołow|agm|gel/i, 'kwasowo-ołowiowa'],
]

/** Technologia magazynu po polsku z pola `chemistry` karty („LFP (LiFePO4)” → „litowo-żelazowo-fosforanowa (LiFePO4)”). */
export const technologiaMagazynu = (chemia) => {
  if (!chemia) return null
  const t = TECHNOLOGIE.find(([wzor]) => wzor.test(chemia))
  return t ? t[1] : chemia
}

/**
 * Wszystkie pola załączników o magazynie (Enea C, Energa C, PGE WZM-ME, Stoen), wspólne dla operatorów.
 * Źródła: karta katalogowa (technologia, moce, sprawność, DoD, cykle), zestaw (fazy, typ falownika),
 * formularz (moc magazynu), wyliczenia i założenia właściciela (2026-10-09):
 * - cos φ = 1 (praca bez mocy biernej), więc kompensacja cos φ 1 i tg φ 0;
 * - jeden pełny cykl na dobę: z niego planowana maksymalna roczna energia (pojemność użytkowa × 365,
 *   pobór z sieci podzielony przez sprawność) i przewidywany czas eksploatacji (cykle z karty ÷ 365);
 * - minimalny stopień naładowania 100 − DoD z karty, maksymalny 100%.
 * Moc ładowania i rozładowania: z karty, a przy samym prądzie U × I (granica po stronie baterii, `zPradu`).
 * Pola bez danych zostają null: wpisuje je człowiek. Wszystko można poprawić w podglądzie PDF.
 */
export const parametryMagazynu = (sprzet, klient = {}) => {
  const m = sprzet.magazyn
  if (!m) return null
  const k = m.dane || {}
  const sztuk = m.sztuk || 1
  const razy = (v) => (v != null ? Math.round(v * sztuk * 100) / 100 : null)
  const ladowanieSzt = k.moc_ladowania_kw ?? k.moc_ladowania_z_pradu_kw ?? null
  const rozladowanieSzt = m.mocKwSzt ?? m.mocMaksSzt ?? null
  const sprawnosc = k.sprawnosc_procent ?? null
  const uzytkowaSzt = k.energia_uzytkowa_kwh ?? (m.kwhSzt && k.dod_procent ? (m.kwhSzt * k.dod_procent) / 100 : m.kwhSzt)
  const uzytkowa = uzytkowaSzt ? uzytkowaSzt * sztuk : null
  const doSieciKwh = uzytkowa ? Math.round(uzytkowa * 365) : null
  const zSieciKwh = uzytkowa ? Math.round((uzytkowa * 365) / ((sprawnosc || 100) / 100)) : null
  const hybrydowy = /hybryd/i.test(sprzet.falownik?.typ || '')
  const typFalownika = /all.?in.?one/i.test(m.typ || '') ? `wbudowany w magazyn ${[m.producent, m.model].filter(Boolean).join(' ')}`.trim()
    : hybrydowy ? `hybrydowy ${[sprzet.falownik.producent, sprzet.falownik.model].filter(Boolean).join(' ')}`.trim() : null
  return {
    technologia: technologiaMagazynu(k.technologia),
    mocLacznaKw: mocMagazynuKlienta(klient),
    mocLadowaniaKw: razy(ladowanieSzt),
    ladowanieZPradu: k.moc_ladowania_kw == null && k.moc_ladowania_z_pradu_kw != null,
    mocRozladowaniaKw: razy(rozladowanieSzt),
    rozladowanieZPradu: m.mocKwSzt == null && m.mocMaksSzt != null,
    sprawnoscProcent: sprawnosc,
    socMinProcent: k.dod_procent ? Math.round(100 - k.dod_procent) : null,
    socMaxProcent: 100,
    cosFi: 1,
    tgFi: 0,
    cykleNaDobe: 1,
    latEksploatacji: k.cykle ? Math.round(k.cykle / 365) : null,
    energiaZSieciKwh: zSieciKwh,
    energiaDoSieciKwh: doSieciKwh,
    typFalownika,
    fazy: sprzet.falownik?.fazyZnane ? sprzet.falownik.fazy : null,
  }
}

/** Kod pocztowy „61123” albo „61-123” jako ['61', '123'], inaczej null. */
export const czesciKodu = (kod) => {
  const m = String(kod || '').trim().match(/^(\d{2})-?(\d{3})$/)
  return m ? [m[1], m[2]] : null
}

/** Puste wartości nie nadpisują pól (zostają do ręcznego wpisania). */
export const bezPustych = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v != null && String(v).trim() !== ''))

/**
 * Pola parametrów magazynu w załączniku C Enei (jednostki są w etykietach wierszy). Pozycje 10–18 wzór
 * wymaga tylko przy wniosku o warunki: wpisujemy je, gdy tryb nie jest czystym zgłoszeniem.
 */
const polaParametrowEnea = (p, przyWniosku) => (p ? {
  'Rodzaj technologii magazynowania': p.technologia || '',
  'Sprawność jednokrotnego cyklu magazynowania energii': liczbaPl(p.sprawnoscProcent, 1),
  'Maksymalna moc, która może być odbierana z sieci (DC)': liczbaPl(p.mocLadowaniaKw, 2),
  'Maksymalna moc, która może być oddawana do sieci (DC)': liczbaPl(p.mocRozladowaniaKw, 2),
  'Planowana maksymalna roczna ilość energii elektrycznej odbieranej z sieci': p.energiaZSieciKwh ? String(p.energiaZSieciKwh) : '',
  'Planowana maksymalna roczna ilość energii elektrycznej oddawanej do sieci': p.energiaDoSieciKwh ? String(p.energiaDoSieciKwh) : '',
  ...(przyWniosku ? {
    'Minimalny stopień naładowania': p.socMinProcent != null ? String(p.socMinProcent) : '',
    'Maksymalny stopień naładowania': String(p.socMaxProcent),
    'Znamionowy współczynnik mocy cosinus fi': String(p.cosFi),
    'Stopień skompensowania mocy biernej dla energii elektrycznej oddawanej z sieci cos fi': String(p.cosFi),
    'Stopień skompensowania mocy biernej dla energii elektrycznej odbieranej z sieci tg fi związanej z odbiorem energii elektrycznej czynnej na potrzeby własne instalacji magazynu energii elektrycznej': String(p.tgFi),
    'Dopuszczalna liczba cykli instalacji magazynowania energii elektrycznej, w danej jednostce czasu': String(p.cykleNaDobe),
    'Przewidywany czas eksploatacji instalacji magazynowania energii elektrycznejk': p.latEksploatacji ? String(p.latEksploatacji) : '',
  } : {}),
} : {})

/**
 * Polecenia wypełnienia formularza Enea WZ-MI (wersja 21.05.2026) i załącznika C (magazyn).
 * Zwraca { tekst: {pole: wartość}, wybor: {grupa: opcja}, zaznacz: [pola wyboru], zalacznikC: {...} | null }.
 * Nazwy pól i opcji odczytane z PDF-u Enei (pdf-lib), opis w `VV/dane_badawcze/osd_zgloszenia/`.
 */
export const poleceniaEnea = (sprzet, klient = {}, dzis = new Date()) => {
  const tekst = {}
  const wybor = {}
  const zaznacz = []
  const d = czesciDaty(dzis)
  const { moduly, falownik, magazyn } = sprzet

  // Moc do porównania z przyłączeniową (art. 7 ust. 8d12): przy przekroczeniu zaznaczamy „wniosek”.
  const mocMagazynu = mocMagazynuKlienta(klient)
  const ocena = ocenaWniosku(sprzet, klient)
  // 0 = zgłoszenie, 1 = wniosek o warunki przyłączenia, 2 = zgłoszenie, a gdy warunki nie są spełnione, wniosek
  // (wybieramy, gdy nie znamy mocy przyłączeniowej albo mocy magazynu: nie zgadujemy trybu).
  wybor['rodzaj wniosku'] = { wniosek: 1, zgloszenie: 0 }[ocena.tryb] ?? 2
  if (d) Object.assign(tekst, { 'data dzień': d.dz, 'data miesiąc': d.mies, 'data rok': d.rok, data: `${d.dz}.${d.mies}.${d.rok}` })
  // Pole „miejscowość” jest jedno na dwa miejsca (miejscowość wniosku i lokalizacji): wpisujemy miejscowość instalacji.
  const inst = adresInstalacji(klient)
  if (inst.miejscowosc) tekst['miejscowość'] = inst.miejscowosc

  // 1. Wnioskodawca: trzy linie „kratek” po 36 znaków, drukowanymi literami (tak każe wzór). Imię i nazwisko
  // w pierwszej, w dwóch kolejnych adres, gmina i kontakt według ważności; co się nie zmieści, zostaje pominięte
  // (telefon i e-mail są we wzorze dobrowolne). PESEL zostaje pusty.
  const [l2, l3] = rozlozWLinie([
    [klient.ulica, klient.nrDomu].filter(Boolean).join(' '),
    [klient.kodPocztowy, klient.miejscowosc].filter(Boolean).join(' '),
    klient.gmina ? `gm. ${klient.gmina}` : '',
    klient.telefon ? `tel. ${klient.telefon}` : '',
    klient.email || '',
  ], 2, KRATKI_WNIOSKODAWCY)
  tekst['dane wnioskodawcy 1'] = (klient.imieNazwisko || '').toUpperCase().slice(0, KRATKI_WNIOSKODAWCY)
  // Dwa pola PESEL obok siebie: wnioskodawca i ewentualny współwłaściciel. Tylko poprawne numery.
  if (peselPoprawny(klient.pesel)) tekst.pesel1 = cyfryPesel(klient.pesel)
  if (peselPoprawny(klient.pesel2)) tekst.pesel2 = cyfryPesel(klient.pesel2)
  tekst['dane wnioskodawcy2'] = l2.toUpperCase()
  tekst['dane wnioskodwacy3'] = l3.toUpperCase()

  // 4. Lokalizacja mikroinstalacji.
  if (klient.instalacjaTaSama !== false) zaznacz.push('lokalizacja obiektu jak powyżej')
  tekst['ulica osiedle plac itp'] = inst.ulica || ''
  tekst['nr domu  lokalu'] = inst.nr || ''
  tekst['numer działki'] = klient.numerDzialki || ''
  tekst.gmina = inst.gmina || ''
  const kod = czesciKodu(inst.kod)
  if (kod) Object.assign(tekst, { 'kod pocztowy1': kod[0], 'kod pocztowy2': kod[1] })

  tekst['numer PPE'] = klient.ppe || klient.numerLicznika || ''
  wybor['typ instalacji odbiorcy końcowego'] = (klient.instalacjaOdbiorcza || (falownik.fazy === 1 ? '1' : '3')) === '1' ? 0 : 1
  if (klient.mocPrzylaczeniowa) tekst['moc przyłączeniowa instalacji odbiorczej'] = liczbaPl(klient.mocPrzylaczeniowa, 2)

  // 5. Rodzaj źródła i urządzenia.
  zaznacz.push('energia promieniowania słonecznego')
  wybor['magazyn energii'] = magazyn ? 0 : 1
  Object.assign(tekst, {
    'Producent 1': moduly.producent,
    'Typ i model jednostki wytwórczej 1': moduly.model ? `moduł PV ${moduly.model}` : 'moduł fotowoltaiczny',
    'Jednostkowa moc zainstalowana 1': liczbaPl(moduly.mocKw, 3),
    'Liczba 1': moduly.liczba ? String(moduly.liczba) : '',
    'Sumaryczna moc zainstalowana 1': liczbaPl(moduly.sumaKw, 2),
    // „Razem” według art. 7 ust. 8d12: z mocą magazynu, gdy się ją dolicza.
    'Sumaryczna moc zainstalowana razem': liczbaPl(ocena.stan === 'gotowe' ? ocena.lacznie : moduly.sumaKw, 2),
  })
  zaznacz.push('Nowe jednostki 1')
  if (magazyn) {
    // Moc magazynu w kW bierze się z karty katalogowej: zostawiamy do uzupełnienia.
    Object.assign(tekst, {
      'Producent 2': magazyn.producent,
      'Typ i model jednostki wytwórczej 2': `magazyn energii ${magazyn.model || ''}`.trim()
        + (magazyn.kwhSzt ? ` ${liczbaPl(magazyn.kwhSzt, 2)} kWh` : ''),
      'Liczba 2': String(magazyn.sztuk),
      'Jednostkowa moc zainstalowana 2': mocMagazynu ? liczbaPl(mocMagazynu / magazyn.sztuk, 2) : '',
      'Sumaryczna moc zainstalowana 2': mocMagazynu ? liczbaPl(mocMagazynu, 2) : '',
    })
    zaznacz.push('Nowe jednostki 2')
  }
  Object.assign(tekst, {
    'Producent inwertera 1': falownik.producent,
    'Model inwertera/przetwornicy 1': falownik.model,
    'Moc znamionowa 1': liczbaPl(falownik.mocKw, 2),
    [falownik.fazy === 1 ? 'Liczba inwerterów 1-fazowych L1 1' : 'Liczba inwerterów 3-fazowych 1']: String(falownik.sztuk || 1),
  })
  // Kilka falowników: wzór wymaga wspólnego urządzenia sterującego (integratora); dane wpisuje użytkownik.
  if ((falownik.sztuk || 1) > 1) {
    Object.assign(tekst, {
      'Producent urządzenia sterującego': klient.integratorProducent || '',
      'Typ/model integratora': klient.integratorModel || '',
    })
  }
  zaznacz.push('Nowe jednostki 1a')

  const termin = czesciDaty(klient.termin)
  if (termin) {
    Object.assign(tekst, {
      'Przewidywany termin rozpoczęcia poboru/dostarczania energii - dzień': termin.dz,
      'Przewidywany termin rozpoczęcia poboru/dostarczania energii - miesiąc': termin.mies,
      'Przewidywany termin rozpoczęcia poboru/dostarczania energii - rok': termin.rok,
    })
  }
  wybor['Wniosek/zgłoszenie dotyczy'] = klient.obiektNowy ? 0 : 1
  wybor['Oświadczam, że'] = klient.prosument === false ? 1 : 0
  wybor['Energię wytworzoną w mikroinstalacji zamierzam spożytkować na'] = 0 // potrzeby gospodarstwa domowego

  const zalacznikC = magazyn ? {
    tekst: {
      data_4: d ? `${d.dz}.${d.mies}.${d.rok}` : '',
      'Numer załącznika C': '1',
      'Liczba załączników C': '1',
      'Producent magazynu energii elektrycznej': magazyn.producent,
      'Typ/model magazynu energii elekrycznej': magazyn.model,
      'Liczba jednostek': String(magazyn.sztuk),
      'Liczba faz': String(falownik.fazy),
      'Pojemność nominalna': liczbaPl(magazyn.kwhRazem, 2), // jednostka [kWh] jest w kolumnie obok
      'Łączna moc zainstalowana elektryczna magazynu': mocMagazynu ? liczbaPl(mocMagazynu, 2) : '', // [kW] jest w etykiecie wiersza
      'Moc znamionowa pojedynczej jednostki': mocMagazynu ? liczbaPl(mocMagazynu / magazyn.sztuk, 2) : '',
      ...polaParametrowEnea(parametryMagazynu(sprzet, klient), ocena.tryb !== 'zgloszenie'),
    },
  } : null

  return {
    tekst: bezPustych(tekst), wybor, zaznacz,
    zalacznikC: zalacznikC ? { tekst: bezPustych(zalacznikC.tekst) } : null,
  }
}

/** Czego nie wypełniamy i trzeba dopisać albo dołączyć (lista pod przyciskiem). */
export const doUzupelnienia = (sprzet, klient = {}, { pesel = true } = {}) => {
  const lista = []
  // `pesel: false`: wzór operatora nie ma pola PESEL wnioskodawcy (Energa).
  if (pesel && !peselPoprawny(klient.pesel)) lista.push('PESEL (wpisz go w danych wnioskodawcy albo ręcznie we wniosku)')
  lista.push('podpis i data przy podpisie')
  lista.push('schemat elektryczny sprawdzony i podpisany przez instalatora (projekt schematu pobierzesz wyżej)')
  if ((sprzet.falownik.sztuk || 1) > 1 && !(klient.integratorProducent && klient.integratorModel)) {
    lista.push('producent i model integratora (wspólnego urządzenia sterującego falownikami)')
  }
  if (!klient.ppe && !klient.numerLicznika) lista.push('kod PPE albo numer licznika (z rachunku za prąd)')
  if (!klient.mocPrzylaczeniowa) lista.push('moc przyłączeniowa obiektu (z umowy albo rachunku)')
  if (!sprzet.falownik.model) lista.push('dokładny model falownika')
  if (!sprzet.moduly.model) lista.push('dokładny model modułów')
  if (sprzet.magazyn) {
    // Tylko to, czego nie mamy z karty ani z formularza (`parametryMagazynu`).
    const p = parametryMagazynu(sprzet, klient)
    const braki = [
      !p.mocLacznaKw && 'moc magazynu w kW', !p.technologia && 'technologia', p.sprawnoscProcent == null && 'sprawność cyklu',
      p.mocLadowaniaKw == null && 'moc ładowania', p.mocRozladowaniaKw == null && 'moc rozładowania',
    ].filter(Boolean)
    if (braki.length) lista.push(`w załączniku o magazynie: ${braki.join(', ')} (z karty katalogowej)`)
  }
  return lista
}

/**
 * Ściąga danych sprzętu do przepisania w portalu OSD albo we wzorze, którego nie wypełniamy.
 * Grupy z wierszami {etykieta, wartosc}; puste wartości pomijamy, żeby nie podsuwać zgadywania.
 */
export const sciagaDanych = (sprzet) => {
  const { moduly, falownik, magazyn } = sprzet
  const kw = (v, m = 3) => (v ? `${liczbaPl(v, m)} kW` : '')
  const grupy = [
    { tytul: 'Moduły fotowoltaiczne', wiersze: [
      ['Producent', moduly.producent], ['Model', moduly.model], ['Moc jednego modułu', kw(moduly.mocKw)],
      ['Liczba', moduly.liczba ? `${moduly.liczba} szt.` : ''], ['Moc łączna', kw(moduly.sumaKw, 2)],
    ] },
    { tytul: 'Falownik', wiersze: [
      ['Producent', falownik.producent], ['Model', falownik.model], ['Moc znamionowa', kw(falownik.mocKw, 2)],
      ['Liczba', (falownik.sztuk || 1) > 1 ? `${falownik.sztuk} szt.` : ''],
      ['Fazy', falownik.fazyZnane ? (falownik.fazy === 1 ? 'jednofazowy' : 'trójfazowy') : ''],
      ['Certyfikat NC RfG', falownik.certyfikat?.dokument
        ? `${falownik.certyfikat.dokument} (wykaz PTPiREE ${falownik.certyfikat.wykaz})` : ''],
    ] },
  ]
  if (magazyn) {
    grupy.push({ tytul: 'Magazyn energii', wiersze: [
      ['Producent', magazyn.producent], ['Model', magazyn.model],
      ['Pojemność jednej jednostki', magazyn.kwhSzt ? `${liczbaPl(magazyn.kwhSzt, 2)} kWh` : ''],
      ['Moc jednej jednostki (z karty)', kw(magazyn.mocKwSzt, 2)],
      ['Liczba jednostek', `${magazyn.sztuk} szt.`],
      ['Pojemność łączna', magazyn.kwhRazem ? `${liczbaPl(magazyn.kwhRazem, 2)} kWh` : ''],
    ] })
  }
  return grupy.map((g) => ({ ...g, wiersze: g.wiersze.filter(([, v]) => v).map(([etykieta, wartosc]) => ({ etykieta, wartosc })) }))
}

// Wypełnianie wzoru (pola, nadruk na płaskim PDF) żyje we wspólnym rdzeniu `osd/wypelnianie.js`.
export { wypelnijPdf } from './osd/wypelnianie'

/** Pobranie bajtów jako pliku. */
export const pobierzPlik = (bajty, nazwa) => {
  const url = URL.createObjectURL(new Blob([bajty], { type: 'application/pdf' }))
  const a = document.createElement('a')
  a.href = url
  a.download = nazwa
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 2000)
}
